import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import ViewRegisterModal from '../../components/Modal/ProfitLoss';
import ReportRefreshBanner, {
    showRefreshCompletedToast,
} from '../../components/ReportRefreshBanner';
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle';
import { MONTHS } from '../../resources/constants';
import { calculateMaxHeightContent, fileDownloadBlob, lastYears } from '../../resources/functions';
import { currencyFormat } from '../../resources/helper';
import { useReportApi } from '../../resources/hooks/api/useReportApiHook';
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook';
import { getAccountSettings } from '../../store/settings/selectors';
import { getBranch } from '../../store/user/selectors';

interface Props {

}

const COLUMN_OPTIONS = [
    { key: 'current_month', label: 'Current Month' },
    { key: 'ytd', label: 'Year To Date' },
    { key: 'current_month_prior_year', label: 'Current Month Prior Year' },
    { key: 'ytd_prior_year', label: 'Year To Date Prior Year' },
];
const ALL_COLUMN_KEYS = COLUMN_OPTIONS.map(c => c.key);

const ProfitLoss: React.FC<Props> = () => {
    const { profitLossData, downloadReport } = useReportApi();
    const { fetchAccountSettingsData } = useSettingsApi();
    const { t } = useTranslation("screens.profitLoss");
    const previousRefreshInProgressRef = useRef(false);

    const [glProfitLossList, setGlProfitLossList] = useState<any[]>([]);
    const [month, setMonth] = useState<any>(1);
    const [year, setYear] = useState<any>(2024);
    const [searchParams, setSearchParams] = useState<any>({
        month: { value: '01', label:  t('month.01', { ns: 'resources.form-validator' }) },
        year: { value: new Date().getFullYear(), label: '' + new Date().getFullYear() }
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [regenerationPending, setRegenerationPending] = useState<any>(null);
    const [isThirdPartyEnabled, setIsThirdPartyEnabled] = useState<boolean>(false);
    const [isShowViewRegister, setIsShowViewRegister] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [showExportPopover, setShowExportPopover] = useState<boolean>(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(ALL_COLUMN_KEYS);
    const popoverRef = useRef<HTMLDivElement>(null);
    const exportBtnRef = useRef<HTMLButtonElement>(null);
    const [txnRegisterPayload, setTxnRegisterPayload] = useState<{
        startDate: string,
        endDate: string,
        account: number,
        hasEditAccess: boolean
    }>({
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        account: 0,
        hasEditAccess: false
    });

    const branchData = useSelector(getBranch)
    const accountSettings = useSelector(getAccountSettings)
    const branchId = +branchData['id'];
    const isRefreshInProgress = !!accountSettings.bs_refresh_in_progress;
    // The refresh runs in the background: show only the progress banner, never a
    // blocking overlay, so the user keeps full use of the page while it works.
    const showRefreshBanner = isRefreshInProgress;
 const tableContainerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(78));
    })

    const fetchProfitLossList = () => {
        const params: any = {
            "page": 1,
            "per_page": 5000,
            year: searchParams.year['value'],
            month: searchParams.month['value'],
            branch_id: +branchData['id']
        }
        setYear(searchParams.year['value']);
        setMonth(searchParams.month['value']);

        setIsLoading(true);
        profitLossData(params, (message: string, resp: any) => {
            setIsLoading(false);
            // The API answers with regeneration_pending (and no result) while a rebuild
            // owns this month. Show nothing plus an explicit in-progress message rather
            // than the previous period's rows, which would present another month's
            // figures as this one's.
            if (resp?.data?.data?.regeneration_pending) {
                setGlProfitLossList([]);
                setRegenerationPending(resp.data.data.regeneration_pending);
                getSettingsData();
                return;
            }
            setRegenerationPending(null);
            if (resp && resp['data'] && resp['data']['data'] && resp['data']['data']['result']) {
                setGlProfitLossList(resp.data.data.result);
            } else {
                setGlProfitLossList([]);
            }

            if (resp && resp['data'] && resp['data']['data']) {
                setIsThirdPartyEnabled(resp.data.data?.is_third_party_enable ?? false)
            }

        }, (message: string, resp: any) => {
            setGlProfitLossList([]);
            setIsLoading(false);
            toast.error(message);
        })
    }


    const _handleChangeSearchParam = (e: any, type: string) => {
        // Drop the rows for the period being navigated away from - the list is only
        // refetched when Generate is pressed, so leaving them up renders one month's
        // figures under another month's heading.
        setGlProfitLossList([]);
        setRegenerationPending(null);
        if (type === 'month') {
            setSearchParams({
                ...searchParams,
                'month': e
            })
        } else if (type === 'year') {
            setSearchParams({
                ...searchParams,
                'year': e
            })
        }
    }
    const getLastDayOfMonth = (month: any, year: any) => {
        return new Date(year, month, 0).getDate();
    }
    const percentageFormat = (value: any) => {
        return `${value}%`;
    };

    const calculateRatio = (amount: any, budget: any) => {
        // Ensure numerator and denominator are not zero to avoid division by zero error
        if (budget !== 0) {
            const ratio = (amount / budget) * 100;
            return percentageFormat(ratio.toFixed(2));
        } else {
            // Handle division by zero error gracefully
            return percentageFormat((0).toFixed(2))
        }
    };

    const _toggleExportPopover = () => {
        setShowExportPopover(prev => {
            if (!prev) {
                setSelectedColumns(ALL_COLUMN_KEYS);
                return true;
            }
            return false;
        });
    };

    const _closeExportPopover = () => {
        setShowExportPopover(false);
        exportBtnRef.current?.focus();
    };

    const _toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const _toggleAllColumns = () => {
        setSelectedColumns(prev =>
            prev.length === ALL_COLUMN_KEYS.length ? [] : [...ALL_COLUMN_KEYS]
        );
    };

    const _downloadReport = (columnsToExport: string[] = selectedColumns) => {
        if (!columnsToExport.length) return;
        const params: any = {
            year: searchParams.year['value'],
            month: +searchParams.month['value'],
            branch_id: +branchData['id'],
            type: 2, // for profit/loss
            columns: columnsToExport.join(',')
        }

        setIsLoading(true);
        setShowExportPopover(false);
        downloadReport(params, (message: string, resp: any) => {
            setIsLoading(false);
            fileDownloadBlob(resp, `ps-${searchParams.year['value']}-${searchParams.month['value']}`)
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    useEffect(() => {
        if (!showExportPopover) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                _closeExportPopover();
            }
        };
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                exportBtnRef.current &&
                !exportBtnRef.current.contains(e.target as Node)
            ) {
                setShowExportPopover(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showExportPopover]);

    const _handleShowViewRegister = (data: any, startDate: string, hasEditAccess: boolean) => {
        setTxnRegisterPayload({
            startDate: startDate,
            endDate: `${year}-${month}-${getLastDayOfMonth(month, year)}`,
            account: data['account_chart']['id'],
            hasEditAccess: isThirdPartyEnabled ? false : hasEditAccess
        })
        setIsShowViewRegister(true);
    }

    const _handleCloseViewRegister = () => {
        setIsShowViewRegister(false);
        fetchProfitLossList();
    }

    const getSettingsData = (onDone?: () => void) => {
        fetchAccountSettingsData({ branch_id: branchId }, (msg: string, resp: any) => {
            onDone?.();
        }, (msg: string, resp: any) => {
            onDone?.();
        })
    }

    useEffect(() => {
        if (!isRefreshInProgress) return;
        const intervalId = setInterval(() => {
            getSettingsData();
        }, 5000);

        return () => clearInterval(intervalId);
    }, [isRefreshInProgress, branchId])

    useEffect(() => {
        const wasRefreshInProgress = previousRefreshInProgressRef.current;

        if (wasRefreshInProgress && !isRefreshInProgress && branchId) {
            showRefreshCompletedToast();
        }

        previousRefreshInProgressRef.current = isRefreshInProgress;
    }, [isRefreshInProgress, branchId]);


    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                 <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
                    <div>
                        <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">{t("page.title")}</h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="filter-box">
                                    <Select
                                        onChange={(e: any) => _handleChangeSearchParam(e, 'month')}
                                        options={MONTHS}
                                        defaultValue={searchParams['month']}
                                        className={'zindex-10'}
                                    />
                                </div>
                                <div className="filter-box">
                                    <Select
                                        onChange={(e: any) => _handleChangeSearchParam(e, 'year')}
                                        options={lastYears(5)}
                                        defaultValue={searchParams['year']}
                                        className={'zindex-10'}
                                    />
                                </div>

                                <div className="filter-box">
                                    <button type="button" onClick={fetchProfitLossList} className="btn-primary btn btn-lg">{t("filter.generateReport")} &nbsp; <i className="fa fa-search" aria-hidden="true"></i></button>
                                </div>
                                {regenerationPending ? (
                                    <div className="filter-box text-muted">
                                        {t("table.regenerating", {
                                            completed: regenerationPending?.params?.completed ?? 0,
                                            total: regenerationPending?.params?.total ?? 0,
                                            pending: regenerationPending?.params?.pending ?? 0,
                                            context: regenerationPending?.params?.total ? 'progress' : 'queued',
                                        })}
                                    </div>
                                ) : null}
                                {
                                    glProfitLossList.length ? (
                                        <div className="filter-box text-end" style={{ position: 'relative' }}>
                                            <button
                                                ref={exportBtnRef}
                                                type="button"
                                                onClick={_toggleExportPopover}
                                                className="btn-dark btn btn-lg"
                                                aria-haspopup="dialog"
                                                aria-expanded={showExportPopover}
                                            >
                                                {t("filter.exportPdf")} &nbsp;
                                                <i className="fa fa-download" aria-hidden="true"></i>
                                            </button>

                                            {showExportPopover && (
                                                <div
                                                    ref={popoverRef}
                                                    role="dialog"
                                                    aria-label="Select columns to include in PDF export"
                                                    className="pdf-export-popover"
                                                >
                                                    <p className="pdf-export-popover__title">
                                                        Select columns to include
                                                    </p>

                                                    <label className="pdf-export-popover__select-all">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedColumns.length === ALL_COLUMN_KEYS.length}
                                                            onChange={_toggleAllColumns}
                                                            aria-label="Select all or deselect all columns"
                                                        />
                                                        Select All / Deselect All
                                                    </label>

                                                    {COLUMN_OPTIONS.map(col => (
                                                        <label key={col.key} className="pdf-export-popover__option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedColumns.includes(col.key)}
                                                                onChange={() => _toggleColumn(col.key)}
                                                                aria-label={col.label}
                                                            />
                                                            {col.label}
                                                        </label>
                                                    ))}

                                                    {selectedColumns.length === 0 && (
                                                        <p className="pdf-export-popover__error" role="alert">
                                                            Select at least one column
                                                        </p>
                                                    )}

                                                    <div className="pdf-export-popover__footer">
                                                        <button
                                                            type="button"
                                                            className="btn btn-cancel btn-sm"
                                                            onClick={_closeExportPopover}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm"
                                                            disabled={selectedColumns.length === 0}
                                                            onClick={() => _downloadReport(selectedColumns)}
                                                        >
                                                            Export PDF
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : <></>
                                }
                                <div className="filter-btn">
                                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                        <div className="loader"></div>
                                    </div>
                                </div>
                            </div>
                </div>
                {showRefreshBanner && (
                    <ReportRefreshBanner
                        open={showRefreshBanner}
                        total={accountSettings.bs_refresh_total}
                        completed={accountSettings.bs_refresh_completed}
                        startedAt={accountSettings.bs_refresh_started_at}
                    />
                )}
                <div className="executive-dashboard">
                        <div
            className="table-responsive list-table table-scroll"
            style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}
            ref={tableContainerRef}
          >
                        <table className="table table-default table-bordered">
                            <thead>
                                <tr className="align-middle">
                                    <th className="text-center position-fix" colSpan={2}></th>
                                    <th className="text-center" colSpan={4}>{t("table.currentMonth")}<br />{month}/01/{year}  {t("table.to")}  {month}/{getLastDayOfMonth(month, year)}/{year}</th>
                                    <th colSpan={1}></th>
                                    <th className="text-center" colSpan={4}>{t("table.yearToDate")}<br />01/01/{year} {t("table.to")} {month}/{getLastDayOfMonth(month, year)}/{year}</th>
                                    <th colSpan={1}></th>
                                    <th className="text-center" colSpan={2}>{t("table.currentMonthPriorYear")}<br />{month}/01/{year - 1} {t("table.to")} {month}/{getLastDayOfMonth(month, year - 1)}/{year - 1}</th>
                                    <th colSpan={1}></th>
                                    <th className="text-center" colSpan={2}>{t("table.yearToDatePriorYear")}<br />01/01/{year - 1} {t("table.to")} {month}/{getLastDayOfMonth(month, year - 1)}/{year - 1}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    glProfitLossList.length && (
                                        <>
                                            {/* <tr className="align-middle h-60  bs-table-body-SH">

                                                <td className="position-fix" colSpan={2}></td>

                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>
                                                <td className="text-center"><h4 className='under-line'>AMOUNT</h4></td>
                                                <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>

                                                <td colSpan={1}></td>

                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>
                                                <td className="text-center"><h4 className='under-line'>AMOUNT</h4></td>
                                                <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>

                                                <td colSpan={1}></td>

                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>
                                                <td className="text-center"><h4 className='under-line'>AMOUNT</h4></td>

                                                // <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                // <td className="text-center"><h4 className='under-line'>RATIO</h4></td>

                                                <td colSpan={1}></td>

                                                <td className="text-center"><h4 className='under-line'>RATIO</h4></td>
                                                <td className="text-center"><h4 className='under-line'>AMOUNT</h4></td>

                                                // <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                // <td className="text-center"><h4 className='under-line'>RATIO</h4></td>


                                            </tr> */}
                                            {
                                                glProfitLossList.map((data: any, index: number) => {
                                                    const { report_code } = data['account_chart'];
                                                    const shouldHideRow = ['VP', 'VP/IP', 'IP'].includes(report_code) && !data['total_amount'] && !data['current_year_budget_amount'] && !data['ytd_total_amount'] && !data['budget_ytd_total_amount'] && !data['prev_year_total_amount'] && !data['budget_prev_year_total_amount'] && !data['prev_ytd_total_amount'] && !data['budget_prev_ytd_total_amount'] ? 'd-none' : '';
                                                    if (shouldHideRow == '') {
                                                        return (

                                                            <tr className={`position-fix align-middle h-60 bs-table-body-${data['account_chart']['report_code']}`} key={index}>

                                                                <td colSpan={2}><h4 className='sub-data'>{data['account_chart']['name']}{data['account_chart']['account'] == 100000 ? "**":""}</h4></td>
                                                                {
                                                                    ['GH', 'SH'].includes(data['account_chart']['report_code']) ? (data['account_chart']['account'] == 60000 ? (<td colSpan={15}></td>) : (
                                                                        <>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.amount")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.budget")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>

                                                                            <td></td>

                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.amount")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.budget")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>

                                                                            <td></td>

                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.amount")}</h4></td>
                                                                            {/* <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>RATIO</h4></td> */}

                                                                            <td></td>

                                                                            <td className="text-center"><h4 className='under-line'>{t("table.ratio")}</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>{t("table.amount")}</h4></td>
                                                                            {/* <td className="text-center"><h4 className='under-line'>BUDGET</h4></td>
                                                                            <td className="text-center"><h4 className='under-line'>RATIO</h4></td> */}
                                                                        </>
                                                                    )) :
                                                                        (

                                                                            <>
                                                                                <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['ratio'] || 0).toFixed(2))}</h4></td>
                                                                                <td className="text-end">
                                                                                    {
                                                                                        report_code == 'VP' && data['total_amount'] && (
                                                                                            <span className='' onClick={() => _handleShowViewRegister(data, `${year}-${month}-01`, true)}>
                                                                                                <h4 className={`sub-data clickable_link`}>
                                                                                                    {
                                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                                            (data['total_amount'] <= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['total_amount'] || 0).toFixed(2)))})`) :
                                                                                                            (data['total_amount'] >= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['total_amount'] || 0).toFixed(2)))})`)
                                                                                                    }
                                                                                                    {/* {data['total_amount'] >= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['total_amount'].toFixed(2)))})`} */}
                                                                                                </h4>
                                                                                            </span>
                                                                                        ) || <h4 className='sub-data'>
                                                                                            {
                                                                                                (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                                    (data['total_amount'] <= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['total_amount'] || 0).toFixed(2)))})`) :
                                                                                                    (data['total_amount'] >= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['total_amount'] || 0).toFixed(2)))})`)
                                                                                            }
                                                                                            {/* {data['total_amount'] >= 0 ? currencyFormat((data['total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['total_amount'].toFixed(2)))})`} */}
                                                                                        </h4>
                                                                                    }
                                                                                </td>
                                                                                <td className="text-end"><h4 className='sub-data'>
                                                                                    {
                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                            (data['current_year_budget_amount'] <= 0 ? currencyFormat((data['current_year_budget_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['current_year_budget_amount'] || 0).toFixed(2)))})`) :
                                                                                            (data['current_year_budget_amount'] >= 0 ? currencyFormat((data['current_year_budget_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['current_year_budget_amount'] || 0).toFixed(2)))})`)
                                                                                    }
                                                                                    {/* {data['current_year_budget_amount'] >= 0 ? currencyFormat((data['current_year_budget_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['current_year_budget_amount'].toFixed(2)))})`} */}
                                                                                </h4></td>
                                                                                <td className="text-end"><h4 className='sub-data'>{calculateRatio(data['total_amount'] || 0, data['current_year_budget_amount'] || 0)}</h4></td>


                                                                                <td></td>

                                                                                <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['ytd_ratio'] || 0).toFixed(2))}</h4></td>
                                                                                <td className="text-end">
                                                                                    {
                                                                                        report_code == 'VP' && data['ytd_total_amount'] && (
                                                                                            <span className='text-reset' onClick={() => _handleShowViewRegister(data, `${year}-01-01`, false)}>
                                                                                                <h4 className={`sub-data clickable_link`}>
                                                                                                    {
                                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                                            (data['ytd_total_amount'] <= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['ytd_total_amount'] || 0).toFixed(2)))})`) :
                                                                                                            (data['ytd_total_amount'] >= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['ytd_total_amount'] || 0).toFixed(2)))})`)
                                                                                                    }
                                                                                                    {/* {data['ytd_total_amount'] >= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['ytd_total_amount'].toFixed(2)))})`} */}
                                                                                                </h4>
                                                                                            </span>
                                                                                        ) || <h4 className='sub-data'>
                                                                                            {
                                                                                                (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                                    (data['ytd_total_amount'] <= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['ytd_total_amount'] || 0).toFixed(2)))})`) :
                                                                                                    (data['ytd_total_amount'] >= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['ytd_total_amount'] || 0).toFixed(2)))})`)
                                                                                            }
                                                                                            {/* {data['ytd_total_amount'] >= 0 ? currencyFormat((data['ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['ytd_total_amount'].toFixed(2)))})`} */}
                                                                                        </h4>
                                                                                    }
                                                                                </td>
                                                                                <td className="text-end"><h4 className='sub-data'>
                                                                                    {
                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                            (data['budget_ytd_total_amount'] <= 0 ? currencyFormat((data['budget_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['budget_ytd_total_amount'] || 0).toFixed(2)))})`) :
                                                                                            (data['budget_ytd_total_amount'] >= 0 ? currencyFormat((data['budget_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['budget_ytd_total_amount'] || 0).toFixed(2)))})`)
                                                                                    }
                                                                                    {/* {data['budget_ytd_total_amount'] >= 0 ? currencyFormat((data['budget_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['budget_ytd_total_amount'].toFixed(2)))})`} */}
                                                                                </h4></td>
                                                                                <td className="text-end"><h4 className='sub-data'>{calculateRatio(data['ytd_total_amount'] || 0, data['budget_ytd_total_amount'] || 0)}</h4></td>

                                                                                <td></td>

                                                                                <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['prev_year_ratio'] || 0).toFixed(2))}</h4></td>
                                                                                {/* {calculateRatio(data['prev_year_total_amount'] || 0, data['budget_prev_year_total_amount'] || 0)} */}
                                                                                <td className="text-end"><h4 className='sub-data'>
                                                                                    {
                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                            (data['prev_year_total_amount'] <= 0 ? currencyFormat((data['prev_year_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['prev_year_total_amount'] || 0).toFixed(2)))})`) :
                                                                                            (data['prev_year_total_amount'] >= 0 ? currencyFormat((data['prev_year_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['prev_year_total_amount'] || 0).toFixed(2)))})`)
                                                                                    }
                                                                                    {/* {data['prev_year_total_amount'] >= 0 ? currencyFormat((data['prev_year_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['prev_year_total_amount'].toFixed(2)))})`} */}
                                                                                </h4></td>
                                                                                {/* <td className="text-end"><h4 className='sub-data'>
                                                                            {data['budget_prev_year_total_amount'] >= 0 ? currencyFormat((data['budget_prev_year_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['budget_prev_year_total_amount'].toFixed(2)))})`}
                                                                            </h4></td>
                                                                            <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['prev_year_ratio'] || 0).toFixed(2))}</h4></td> */}

                                                                                <td></td>

                                                                                <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['prev_ytd_ratio'] || 0).toFixed(2))}</h4></td>
                                                                                {/* {calculateRatio(data['prev_ytd_total_amount'] || 0, data['budget_prev_ytd_total_amount'] || 0)} */}
                                                                                <td className="text-end"><h4 className='sub-data'>
                                                                                    {
                                                                                        (data['account_chart']['account'] >= 9902 && data['account_chart']['account'] <= 9964) ?
                                                                                            (data['prev_ytd_total_amount'] <= 0 ? currencyFormat((data['prev_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['prev_ytd_total_amount'] || 0).toFixed(2)))})`) :
                                                                                            (data['prev_ytd_total_amount'] >= 0 ? currencyFormat((data['prev_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs((data['prev_ytd_total_amount'] || 0).toFixed(2)))})`)
                                                                                    }

                                                                                    {/* {data['prev_ytd_total_amount'] >= 0 ? currencyFormat((data['prev_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['prev_ytd_total_amount'].toFixed(2)))})`} */}
                                                                                </h4></td>
                                                                                {/* <td className="text-end"><h4 className='sub-data'>{data['budget_prev_ytd_total_amount'] >= 0 ? currencyFormat((data['budget_prev_ytd_total_amount'] || 0).toFixed(2)) : `(${currencyFormat(Math.abs(data['budget_prev_ytd_total_amount'].toFixed(2)))})`}
                                                                                </h4>
                                                                            </td>
                                                                            <td className="text-end"><h4 className='sub-data'>{percentageFormat((data['prev_ytd_ratio'] || 0).toFixed(2))}</h4></td> */}

                                                                            </>
                                                                        )
                                                                }
                                                            </tr>
                                                        );
                                                    }
                                                })
                                            }
                                        </>

                                    ) || (
                                        <tr className="align-middle h-60">
                                            <td colSpan={16} className='text-center'><span>{t("table.notAvailable")}</span></td>
                                        </tr>

                                    )
                                }

                            </tbody>
                        </table>
                    </div>
                       <ScrollToggle scrollRef={tableContainerRef}/>
                    <div className='mt-2'>
                        <span><strong>{t("executiveDashboard.ebitdaNote")}</strong></span>
                    </div>
                    
                    {
                        isShowViewRegister ?
                            <ViewRegisterModal
                                show={isShowViewRegister}
                                onHide={() => _handleCloseViewRegister()}
                                startDate={txnRegisterPayload.startDate}
                                endDate={txnRegisterPayload.endDate}
                                account={txnRegisterPayload.account}
                                hasEditAccess={txnRegisterPayload.hasEditAccess}
                                header={t("transactionsRegister")}
                            /> : <></>
                    }
                </div>
            </div>
        </div>

    )
}

export default ProfitLoss;
